import {useCallback, useEffect, useMemo, useState} from 'react';

import {AuthSession} from '../App';
import {nameFromEmail} from '../lib/aggregate';
import {Role, UserGroup, UserProfile, patchUser} from '../lib/api';

import {Spinner} from './Spinner';
import {UserAccessChip, UserChip} from './UserChip';

interface Props {
    session: AuthSession;
    users: UserProfile[] | undefined;
    loading: boolean;
    error?: string;
    twingateDown: boolean;
    onReload: () => void;
    /** Surgical update — App keeps the canonical list, we just notify. */
    onUserUpdated: (u: UserProfile) => void;
}

interface RowDraft {
    role: Role;
    group: UserGroup;
    saving: boolean;
    error?: string;
}

type RoleFilter = 'all' | Role;
type GroupFilter = 'all' | UserGroup;

/**
 * Admin-only Users page. Lists everyone in `access-ops-user-profiles` and
 * lets an admin change their role + group inline. Role and group edits are
 * pill-toggle chips matching the rest of the design language; selectors
 * along the top filter the table by free-text email/name, role, and group.
 *
 * Data fetch + caching live in App; this component just renders props and
 * dispatches edits. Drafts and filter selections stay local — they only
 * matter while the page is mounted.
 *
 * Adding brand-new emails is a CLI step (`aws dynamodb put-item` on
 * `access-ops-user-profiles`) — see CLAUDE.md.
 */
export function UsersPage({
    session,
    users,
    loading,
    error,
    twingateDown,
    onReload,
    onUserUpdated,
}: Props) {
    const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});

    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
    const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');

    // Initialize draft rows when new users appear and prune drafts for users
    // that no longer exist. We deliberately don't reset existing drafts on
    // every users change — that would clobber in-progress edits on row B
    // when row A's save round-trips and updates the App-level users array.
    useEffect(() => {
        if (!users) {
            return;
        }
        setDrafts(prev => {
            const next: Record<string, RowDraft> = {};
            for (const u of users) {
                next[u.email] = prev[u.email] ?? {role: u.role, group: u.group, saving: false};
            }
            return next;
        });
    }, [users]);

    const update = (email: string, patch: Partial<RowDraft>) => {
        setDrafts(prev => ({...prev, [email]: {...prev[email], ...patch}}));
    };

    const save = useCallback(
        async (user: UserProfile) => {
            const draft = drafts[user.email];
            if (!draft || (draft.role === user.role && draft.group === user.group)) {
                return;
            }
            update(user.email, {saving: true, error: undefined});
            try {
                const updated = await patchUser(
                    session.idToken,
                    user.email,
                    draft.role,
                    draft.group
                );
                onUserUpdated(updated);
                update(user.email, {
                    saving: false,
                    role: updated.role,
                    group: updated.group,
                    error: undefined,
                });
            } catch (err) {
                update(user.email, {
                    saving: false,
                    error: err instanceof Error ? err.message : String(err),
                });
            }
        },
        [drafts, session.idToken, onUserUpdated]
    );

    // Client-side filtering. Profile count is small (tens, not thousands), so
    // a useMemo over the whole list is plenty.
    const filteredUsers = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (users ?? []).filter(u => {
            if (roleFilter !== 'all' && u.role !== roleFilter) {
                return false;
            }
            if (groupFilter !== 'all' && u.group !== groupFilter) {
                return false;
            }
            if (!q) {
                return true;
            }
            const name = nameFromEmail(u.email).toLowerCase();
            return u.email.toLowerCase().includes(q) || name.includes(q);
        });
    }, [users, search, roleFilter, groupFilter]);

    if (twingateDown) {
        return (
            <main className="page">
                <div className="twingate-prompt">
                    <h2>Подключите Twingate</h2>
                    <p>API недоступен — подключись и нажми Reload.</p>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={onReload}
                        disabled={loading}
                    >
                        Reload
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main className="page">
            <div className="page-head">
                <h1>Users</h1>
                <div className="spacer" />
                <button type="button" className="btn-reload" onClick={onReload} disabled={loading}>
                    {loading ? 'Loading…' : 'Reload'}
                </button>
            </div>
            <div className="page-subhead">
                Assigns each user a role and a group. Trusted users have their access requests
                auto-approved. New users appear here automatically the first time they sign in.
            </div>

            <div className="toolbar">
                <div className="search">
                    <svg viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                        <path
                            d="M20 20l-3.5-3.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                    <input
                        type="text"
                        placeholder="Filter by email or name…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <Pills
                    label="Role"
                    value={roleFilter}
                    onChange={setRoleFilter}
                    options={[
                        {value: 'all', label: 'All'},
                        {value: 'user', label: 'user'},
                        {value: 'admin', label: 'admin'},
                    ]}
                />

                <Pills
                    label="Group"
                    value={groupFilter}
                    onChange={setGroupFilter}
                    options={[
                        {value: 'all', label: 'All'},
                        {value: 'needs-approval', label: 'needs approval'},
                        {value: 'trusted', label: 'trusted'},
                    ]}
                />

                <div className="toolbar-spacer" />
                <div className="toolbar-summary">
                    {filteredUsers.length} of {(users ?? []).length}
                </div>
            </div>

            <div className="card">
                {loading ? (
                    <Spinner label="Loading users…" />
                ) : error ? (
                    <div className="empty">
                        <h3>Failed to load</h3>
                        <div>{error}</div>
                    </div>
                ) : (users ?? []).length === 0 ? (
                    <div className="empty">
                        <h3>No profiles yet</h3>
                        <div>
                            Bootstrap one via <code>aws dynamodb put-item</code>.
                        </div>
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div className="empty">
                        <h3>No matches</h3>
                        <div>Adjust the search box or the filters above.</div>
                    </div>
                ) : (
                    <table className="tbl">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Role</th>
                                <th>Group</th>
                                <th>Last updated</th>
                                <th className="col-actions">Save</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map(u => {
                                const draft = drafts[u.email] ?? {
                                    role: u.role,
                                    group: u.group,
                                    saving: false,
                                };
                                const dirty = draft.role !== u.role || draft.group !== u.group;
                                return (
                                    <tr key={u.email}>
                                        <td>
                                            <UserChip chip={buildUserChip(u.email)} hideBadge />
                                        </td>
                                        <td>
                                            <ToggleGroup
                                                value={draft.role}
                                                disabled={draft.saving}
                                                options={[
                                                    {value: 'user', label: 'user'},
                                                    {value: 'admin', label: 'admin'},
                                                ]}
                                                onChange={v => update(u.email, {role: v as Role})}
                                            />
                                        </td>
                                        <td>
                                            <ToggleGroup
                                                value={draft.group}
                                                disabled={draft.saving}
                                                options={[
                                                    {value: 'needs-approval', label: 'needs approval'},
                                                    {value: 'trusted', label: 'trusted'},
                                                ]}
                                                onChange={v =>
                                                    update(u.email, {group: v as UserGroup})
                                                }
                                            />
                                        </td>
                                        <td className="when-abs">
                                            {u.updatedAt
                                                ? new Date(u.updatedAt).toLocaleString()
                                                : '—'}
                                            {u.updatedBy ? ` by ${u.updatedBy}` : ''}
                                            {draft.error && (
                                                <div className="form-error">{draft.error}</div>
                                            )}
                                        </td>
                                        <td className="col-actions">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={!dirty || draft.saving}
                                                onClick={() => save(u)}
                                            >
                                                {draft.saving ? 'Saving…' : 'Save'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </main>
    );
}

/**
 * Builds a UserChip shape for a profile row. We don't have grant history here,
 * just the email, so we pick a neutral kind (`data-access`) and stamp it as
 * permanent so AccessChip's expiry styling doesn't kick in.
 */
function buildUserChip(email: string): UserAccessChip {
    return {
        email,
        name: nameFromEmail(email),
        kind: 'data-access',
        permanent: true,
        daysRemaining: Number.POSITIVE_INFINITY,
        hoursRemaining: Number.POSITIVE_INFINITY,
        fromBastion: false,
        grants: [],
    };
}

/**
 * Pill-toggle group used both in the filter bar and inline in each row for
 * role / group editing. Styled via `.chip-toggle` (+ `.on` for the active
 * pill) — see `grants.css`.
 */
function ToggleGroup<T extends string>({
    value,
    disabled,
    options,
    onChange,
}: {
    value: T;
    disabled?: boolean;
    options: {value: T; label: string}[];
    onChange: (next: T) => void;
}) {
    return (
        <div className="toggle-group">
            {options.map(opt => (
                <button
                    key={opt.value}
                    type="button"
                    className={`chip-toggle${value === opt.value ? ' on' : ''}`}
                    disabled={disabled || value === opt.value}
                    onClick={() => onChange(opt.value)}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

function Pills<T extends string>(props: {
    label: string;
    value: T;
    options: {value: T; label: string}[];
    onChange: (next: T) => void;
}) {
    return (
        <div className="filter-pills">
            <span className="filter-label">{props.label}</span>
            <ToggleGroup
                value={props.value}
                options={props.options}
                onChange={props.onChange}
            />
        </div>
    );
}
